// src/pages/admin/edit-inmobiliaria.tsx

import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { doc, getDoc, updateDoc, collection, getDocs, query, where, deleteDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../../firebaseConfig';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useNavigate, useParams } from 'react-router-dom';
import Swal from 'sweetalert2';
import FooterTop from '../../components/footer-top';
import Footer from '../../components/footer';
import UserNav from '../../components/navbar/user-nav';
import { useUser } from '../../context/UserContext';

// Validación
const schema = yup.object().shape({
  nombre: yup.string().required('Nombre requerido'),
  descripcion: yup.string().required('Descripción requerida'),
  telefono: yup.string().min(8, 'Mínimo 8 caracteres').required('Teléfono requerido'),
  correo: yup.string().email('Correo inválido').required('Correo requerido'),
  direccion: yup.string().url('Debe ser una URL válida').required('URL requerida'),
  años_experiencia: yup.number().typeError('Debe ser un número').min(0).required('Campo requerido'),
  sitio_web: yup.string().url('Debe ser una URL válida').optional().default(''),
  redes: yup.object().shape({
    facebook: yup.string().url('URL inválida').optional().default(''),
    instagram: yup.string().url('URL inválida').optional().default('')
  })
});

type FormData = {
  nombre: string;
  descripcion: string;
  telefono: string;
  correo: string;
  direccion: string;
  años_experiencia: number;
  sitio_web?: string;
  redes: {
    facebook?: string;
    instagram?: string;
  };
};

interface Asesor {
  id: string;
  nombre: string;
  correo: string;
  telefono: string;
  foto?: string;
  activo: boolean;
}

interface Asociado {
  id: string;
  nombre: string;
  correo: string;
  telefono: string;
  foto?: string;
  activo: boolean;
}

interface Propiedad {
  id: string;
  titulo: string;
  tipo: string;
  precio: number;
  ciudad: string;
  imagen?: string;
}

interface Proyecto {
  id: string;
  nombre: string;
  descripcion: string;
  ubicacion: string;
  imagen?: string;
}

export default function EditInmobiliaria() {
  const { id } = useParams();
  const { user } = useUser();
  const navigate = useNavigate();
  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors }
  } = useForm<FormData>({
    resolver: yupResolver(schema) as any
  });

  const [autorizado, setAutorizado] = useState(false);
  const [loading, setLoading] = useState(true);
  const [originalData, setOriginalData] = useState<FormData | null>(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [activeTab, setActiveTab] = useState('general');

  // Verificar si el usuario es admin
  const isAdmin = user?.tipo?.includes('admin') || user?.rol === 'ADMIN';

  // Photo upload
  const [fotoURL, setFotoURL] = useState('');
  const [uploadingFoto, setUploadingFoto] = useState(false);

  // Active status
  const [activo, setActivo] = useState(true);

  // Members and properties
  const [afiliados, setAfiliados] = useState<Asesor[]>([]);
  const [asociados, setAsociados] = useState<Asociado[]>([]);
  const [propiedades, setPropiedades] = useState<Propiedad[]>([]);
  const [proyectos, setProyectos] = useState<Proyecto[]>([]);

  // Filters
  const [filtroAfiliado, setFiltroAfiliado] = useState('');
  const [filtroAsociado, setFiltroAsociado] = useState('');
  const [filtroEstadoAfiliado, setFiltroEstadoAfiliado] = useState<'todos' | 'activos' | 'inactivos'>('todos');
  const [filtroEstadoAsociado, setFiltroEstadoAsociado] = useState<'todos' | 'activos' | 'inactivos'>('todos');

  // Pagination
  const [paginaAfiliados, setPaginaAfiliados] = useState(1);
  const [paginaAsociados, setPaginaAsociados] = useState(1);
  const itemsPorPagina = 5;

  // Fetch inmobiliaria data
  useEffect(() => {
    const fetchInmobiliaria = async () => {
      try {
        const snap = await getDoc(doc(db, 'inmobiliaria', id as string));

        if (!snap.exists()) {
          Swal.fire({
            icon: 'error',
            title: 'No encontrada',
            text: 'La inmobiliaria no existe.',
            confirmButtonColor: '#d33'
          }).then(() => navigate('/dashboard'));
          setLoading(false);
          return;
        }

        const data = snap.data();

        // ✅ Authorization using doc.id
        const isAdmin = user?.tipo?.includes('admin');
        const isAsociadoAutorizado = user?.tipo?.includes('asociado') && user?.inmobiliaria_doc_id === id;
        const accesoPermitido = isAdmin || isAsociadoAutorizado;

        if (!accesoPermitido) {
          setAutorizado(false);
          setLoading(false);
          return;
        }

        setAutorizado(true);

        const formData: FormData = {
          nombre: data.nombre || '',
          descripcion: data.descripcion || '',
          telefono: data.telefono || '',
          correo: data.correo || '',
          direccion: data.direccion || '',
          años_experiencia: data.años_experiencia || 0,
          sitio_web: data.sitio_web || '',
          redes: {
            facebook: data.redes?.facebook || '',
            instagram: data.redes?.instagram || ''
          }
        };

        setOriginalData(formData);
        setFotoURL(data.foto || '');
        setActivo(data.activo !== false); // Default to true if not set

        for (const key in formData) {
          if (key === 'redes') {
            setValue('redes.facebook', formData.redes.facebook);
            setValue('redes.instagram', formData.redes.instagram);
          } else if (key in formData) {
            setValue(key as keyof FormData, formData[key as keyof FormData]);
          }
        }

        setLoading(false);
      } catch (err) {
        console.error('Error al cargar inmobiliaria:', err);
        setLoading(false);
      }
    };

    if (id && user) fetchInmobiliaria();
  }, [id, user, navigate, setValue]);

  // Fetch afiliados
  useEffect(() => {
    const fetchAfiliados = async () => {
      if (!id || !autorizado) return;
      try {
        const q = query(
          collection(db, 'asesor'),
          where('inmobiliaria_doc_id', '==', id)
        );
        const snap = await getDocs(q);
        // Filtrar solo activos o sin estatus (por defecto activos)
        const data = snap.docs
          .filter(doc => {
            const docData = doc.data();
            return !docData.estatus || docData.estatus === 'activo';
          })
          .map(doc => ({ id: doc.id, ...doc.data() } as Asesor));
        setAfiliados(data);
      } catch (err) {
        console.error('Error al cargar afiliados:', err);
      }
    };
    fetchAfiliados();
  }, [id, autorizado]);

  // Fetch asociados
  useEffect(() => {
    const fetchAsociados = async () => {
      if (!id || !autorizado) return;
      try {
        const q = query(collection(db, 'asociado'), where('inmobiliaria_doc_id', '==', id));
        const snap = await getDocs(q);
        const data = snap.docs.map(doc => ({ id: doc.id, ...doc.data() } as Asociado));
        setAsociados(data);
      } catch (err) {
        console.error('Error al cargar asociados:', err);
      }
    };
    fetchAsociados();
  }, [id, autorizado]);

  // Fetch propiedades
  useEffect(() => {
    const fetchPropiedades = async () => {
      if (!id || !autorizado) return;
      try {
        const q = query(collection(db, 'propiedades'), where('inmobiliaria_doc_id', '==', id));
        const snap = await getDocs(q);
        const data = snap.docs.map(doc => ({ id: doc.id, ...doc.data() } as Propiedad));
        setPropiedades(data);
      } catch (err) {
        console.error('Error al cargar propiedades:', err);
      }
    };
    fetchPropiedades();
  }, [id, autorizado]);

  // Fetch proyectos
  useEffect(() => {
    const fetchProyectos = async () => {
      if (!id || !autorizado) return;
      try {
        const q = query(collection(db, 'proyectos'), where('inmobiliaria_doc_id', '==', id));
        const snap = await getDocs(q);
        const data = snap.docs.map(doc => ({ id: doc.id, ...doc.data() } as Proyecto));
        setProyectos(data);
      } catch (err) {
        console.error('Error al cargar proyectos:', err);
      }
    };
    fetchProyectos();
  }, [id, autorizado]);

  // Watch for form changes
  const watchedValues = watch();
  useEffect(() => {
    if (originalData) {
      const hasFormChanges = JSON.stringify(watchedValues) !== JSON.stringify(originalData);
      setHasChanges(hasFormChanges);
    }
  }, [watchedValues, originalData]);

  // Photo upload handler
  const handleFotoChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      Swal.fire({
        icon: 'error',
        title: 'Archivo demasiado grande',
        text: 'La imagen debe pesar menos de 5MB',
        confirmButtonColor: '#d33'
      });
      return;
    }

    setUploadingFoto(true);
    try {
      const storageRef = ref(storage, `inmobiliarias/${id}/${Date.now()}_${file.name}`);
      await uploadBytes(storageRef, file);
      const url = await getDownloadURL(storageRef);

      await updateDoc(doc(db, 'inmobiliaria', id as string), { foto: url });
      setFotoURL(url);

      Swal.fire({
        icon: 'success',
        title: 'Foto actualizada',
        text: 'La foto se subió correctamente',
        confirmButtonColor: '#3085d6'
      });
    } catch (err) {
      console.error('Error al subir foto:', err);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'No se pudo subir la foto',
        confirmButtonColor: '#d33'
      });
    } finally {
      setUploadingFoto(false);
    }
  };

  // Toggle activo status
  const handleToggleActivo = async () => {
    try {
      const nuevoEstado = !activo;
      await updateDoc(doc(db, 'inmobiliaria', id as string), { activo: nuevoEstado });
      setActivo(nuevoEstado);

      Swal.fire({
        icon: 'success',
        title: 'Estado actualizado',
        text: `La inmobiliaria está ahora ${nuevoEstado ? 'activa' : 'inactiva'}`,
        confirmButtonColor: '#3085d6'
      });
    } catch (err) {
      console.error('Error al actualizar estado:', err);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'No se pudo actualizar el estado',
        confirmButtonColor: '#d33'
      });
    }
  };

  // Toggle member status
  const handleToggleMemberStatus = async (memberId: string, currentStatus: boolean, type: 'asesor' | 'asociado') => {
    try {
      const nuevoEstado = !currentStatus;
      await updateDoc(doc(db, type, memberId), { activo: nuevoEstado });

      if (type === 'asesor') {
        setAfiliados(prev => prev.map(a => a.id === memberId ? { ...a, activo: nuevoEstado } : a));
      } else {
        setAsociados(prev => prev.map(a => a.id === memberId ? { ...a, activo: nuevoEstado } : a));
      }

      Swal.fire({
        icon: 'success',
        title: 'Estado actualizado',
        text: `${type === 'asesor' ? 'Afiliado' : 'Asociado'} ${nuevoEstado ? 'activado' : 'desactivado'}`,
        confirmButtonColor: '#3085d6',
        timer: 2000
      });
    } catch (err) {
      console.error('Error al actualizar estado del miembro:', err);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'No se pudo actualizar el estado',
        confirmButtonColor: '#d33'
      });
    }
  };

  // Delete member
  const handleDeleteMember = async (memberId: string, memberName: string, type: 'asesor' | 'asociado') => {
    const result = await Swal.fire({
      title: '¿Eliminar miembro?',
      text: `¿Estás seguro de eliminar a ${memberName}? Esta acción no se puede deshacer.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Sí, eliminar',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#d33'
    });

    if (!result.isConfirmed) return;

    try {
      await deleteDoc(doc(db, type, memberId));

      if (type === 'asesor') {
        setAfiliados(prev => prev.filter(a => a.id !== memberId));
      } else {
        setAsociados(prev => prev.filter(a => a.id !== memberId));
      }

      Swal.fire({
        icon: 'success',
        title: 'Eliminado',
        text: `${type === 'asesor' ? 'Afiliado' : 'Asociado'} eliminado correctamente`,
        confirmButtonColor: '#3085d6'
      });
    } catch (err) {
      console.error('Error al eliminar miembro:', err);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'No se pudo eliminar al miembro',
        confirmButtonColor: '#d33'
      });
    }
  };

  // Form submit
  const onSubmit = async (data: FormData) => {
    if (!hasChanges) {
      Swal.fire({
        icon: 'info',
        title: 'Sin cambios',
        text: 'No se detectaron modificaciones en el formulario.',
        confirmButtonColor: '#3085d6'
      });
      return;
    }

    try {
      await updateDoc(doc(db, 'inmobiliaria', id as string), data);
      setOriginalData(data);
      setHasChanges(false);

      Swal.fire({
        icon: 'success',
        title: 'Actualizado correctamente',
        text: 'La inmobiliaria fue actualizada con éxito.',
        confirmButtonColor: '#3085d6'
      });
    } catch (err) {
      console.error('Error al actualizar:', err);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Hubo un error al guardar los cambios',
        confirmButtonColor: '#d33'
      });
    }
  };

  // Filter afiliados
  const afiliadosFiltrados = afiliados.filter(a => {
    const matchNombre = a.nombre?.toLowerCase().includes(filtroAfiliado.toLowerCase());
    const matchEstado = filtroEstadoAfiliado === 'todos' ||
                       (filtroEstadoAfiliado === 'activos' && a.activo) ||
                       (filtroEstadoAfiliado === 'inactivos' && !a.activo);
    return matchNombre && matchEstado;
  });

  // Filter asociados
  const asociadosFiltrados = asociados.filter(a => {
    const matchNombre = a.nombre?.toLowerCase().includes(filtroAsociado.toLowerCase());
    const matchEstado = filtroEstadoAsociado === 'todos' ||
                       (filtroEstadoAsociado === 'activos' && a.activo) ||
                       (filtroEstadoAsociado === 'inactivos' && !a.activo);
    return matchNombre && matchEstado;
  });

  // Pagination for afiliados
  const totalPaginasAfiliados = Math.ceil(afiliadosFiltrados.length / itemsPorPagina);
  const afiliadosPaginados = afiliadosFiltrados.slice(
    (paginaAfiliados - 1) * itemsPorPagina,
    paginaAfiliados * itemsPorPagina
  );

  // Pagination for asociados
  const totalPaginasAsociados = Math.ceil(asociadosFiltrados.length / itemsPorPagina);
  const asociadosPaginados = asociadosFiltrados.slice(
    (paginaAsociados - 1) * itemsPorPagina,
    paginaAsociados * itemsPorPagina
  );

  if (loading) return null;

  if (!autorizado) {
    return (
      <div className="container py-5">
        <div className="alert alert-danger text-center shadow-sm rounded">
          <h4 className="mb-2">
            <i className="fa-solid fa-triangle-exclamation me-2"></i>
            Acceso denegado
          </h4>
          <p>No tienes permisos para editar esta inmobiliaria.</p>
          <button className="btn btn-primary mt-3" onClick={() => navigate('/dashboard')}>
            Regresar al Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <style>{`
        .form-control::placeholder {
          color: #6c757d !important;
          opacity: 1 !important;
        }
        .form-control {
          color: #212529 !important;
        }
        .search-input::placeholder {
          color: #6c757d;
          opacity: 1;
        }
        .nav-tabs .nav-link {
          color: #666;
        }
        .nav-tabs .nav-link.active {
          color: #0d6efd;
          font-weight: 600;
        }
        .table-foto {
          width: 50px;
          height: 50px;
          object-fit: cover;
          border-radius: 50%;
        }
        .badge-activo {
          background-color: #28a745;
        }
        .badge-inactivo {
          background-color: #dc3545;
        }
      `}</style>

      <UserNav />

      <div className="page-title">
        <div className="container">
          <h2 className="ipt-title">Administrar Inmobiliaria</h2>
          <span className="ipn-subtitle">Gestión completa de la agencia inmobiliaria</span>
        </div>
      </div>

      <section className="bg-light">
        <div className="container">
          {/* Navigation Tabs */}
          <ul className="nav nav-tabs mb-4" role="tablist">
            <li className="nav-item" role="presentation">
              <button
                className={`nav-link ${activeTab === 'general' ? 'active' : ''}`}
                onClick={() => setActiveTab('general')}
                type="button"
              >
                <i className="fa-solid fa-info-circle me-2"></i>
                Información General
              </button>
            </li>
            <li className="nav-item" role="presentation">
              <button
                className={`nav-link ${activeTab === 'afiliados' ? 'active' : ''}`}
                onClick={() => setActiveTab('afiliados')}
                type="button"
              >
                <i className="fa-solid fa-users me-2"></i>
                Afiliados ({afiliados.length})
              </button>
            </li>
            <li className="nav-item" role="presentation">
              <button
                className={`nav-link ${activeTab === 'asociados' ? 'active' : ''}`}
                onClick={() => setActiveTab('asociados')}
                type="button"
              >
                <i className="fa-solid fa-user-tie me-2"></i>
                Asociados ({asociados.length})
              </button>
            </li>
            <li className="nav-item" role="presentation">
              <button
                className={`nav-link ${activeTab === 'propiedades' ? 'active' : ''}`}
                onClick={() => setActiveTab('propiedades')}
                type="button"
              >
                <i className="fa-solid fa-home me-2"></i>
                Propiedades ({propiedades.length})
              </button>
            </li>
            <li className="nav-item" role="presentation">
              <button
                className={`nav-link ${activeTab === 'proyectos' ? 'active' : ''}`}
                onClick={() => setActiveTab('proyectos')}
                type="button"
              >
                <i className="fa-solid fa-building me-2"></i>
                Proyectos ({proyectos.length})
              </button>
            </li>
          </ul>

          {/* Tab Content */}
          <div className="tab-content">
            {/* TAB 1: General Information */}
            {activeTab === 'general' && (
              <div className="tab-pane fade show active">
                <form onSubmit={handleSubmit(onSubmit)}>
                  <div className="submit-page">
                    {/* Photo Upload */}
                    <div className="form-submit">
                      <h3>Foto de la Inmobiliaria</h3>
                      <div className="submit-section">
                        <div className="text-center mb-3">
                          {fotoURL ? (
                            <img
                              src={fotoURL}
                              alt="Foto inmobiliaria"
                              style={{
                                width: '150px',
                                height: '150px',
                                objectFit: 'cover',
                                borderRadius: '10px',
                                border: '2px solid #ddd'
                              }}
                            />
                          ) : (
                            <div
                              className="d-inline-flex align-items-center justify-content-center bg-light"
                              style={{
                                width: '150px',
                                height: '150px',
                                borderRadius: '10px',
                                border: '2px dashed #ddd'
                              }}
                            >
                              <i className="fa-solid fa-building fa-4x text-muted"></i>
                            </div>
                          )}
                        </div>
                        <div className="text-center">
                          <label className="btn btn-outline-primary btn-sm">
                            <i className="fa-solid fa-upload me-2"></i>
                            {uploadingFoto ? 'Subiendo...' : 'Cambiar Foto'}
                            <input
                              type="file"
                              accept="image/*"
                              onChange={handleFotoChange}
                              style={{ display: 'none' }}
                              disabled={uploadingFoto}
                            />
                          </label>
                          <small className="d-block text-muted mt-2">
                            Máximo 5MB. Formatos: JPG, PNG, GIF
                          </small>
                        </div>
                      </div>
                    </div>

                    {/* Active Status Toggle */}
                    <div className="form-submit">
                      <h3>Estado de la Inmobiliaria</h3>
                      <div className="submit-section">
                        <div className="d-flex align-items-center justify-content-between p-3 bg-white rounded">
                          <div>
                            <strong>Estado Actual:</strong>
                            <span className={`badge ms-2 ${activo ? 'badge-activo' : 'badge-inactivo'}`}>
                              {activo ? 'Activa' : 'Inactiva'}
                            </span>
                          </div>
                          <button
                            type="button"
                            className={`btn btn-sm ${activo ? 'btn-danger' : 'btn-success'}`}
                            onClick={handleToggleActivo}
                          >
                            <i className={`fa-solid fa-${activo ? 'ban' : 'check'} me-2`}></i>
                            {activo ? 'Desactivar' : 'Activar'}
                          </button>
                        </div>
                      </div>
                    </div>

                    {/* Basic Information */}
                    <div className="form-submit">
                      <h3>Información Básica</h3>
                      <div className="submit-section row">
                        <div className="form-group col-md-6">
                          <label>Nombre</label>
                          <input
                            {...register('nombre')}
                            className="form-control"
                            placeholder="Nombre de la inmobiliaria"
                          />
                          <small className="text-danger">{errors.nombre?.message}</small>
                        </div>

                        <div className="form-group col-md-6">
                          <label>Correo</label>
                          <input
                            {...register('correo')}
                            type="email"
                            className="form-control"
                            placeholder="contacto@inmobiliaria.com"
                          />
                          <small className="text-danger">{errors.correo?.message}</small>
                        </div>

                        <div className="form-group col-md-6">
                          <label>Teléfono</label>
                          <input
                            {...register('telefono')}
                            className="form-control"
                            placeholder="7711234567"
                          />
                          <small className="text-danger">{errors.telefono?.message}</small>
                        </div>

                        <div className="form-group col-md-6">
                          <label>Años de experiencia</label>
                          <input
                            {...register('años_experiencia')}
                            type="number"
                            className="form-control"
                            placeholder="Ej. 5"
                          />
                          <small className="text-danger">{errors.años_experiencia?.message}</small>
                        </div>

                        <div className="form-group col-md-12">
                          <label>Descripción</label>
                          <textarea
                            {...register('descripcion')}
                            className="form-control h-120"
                            placeholder="Breve descripción de la inmobiliaria"
                          />
                          <small className="text-danger">{errors.descripcion?.message}</small>
                        </div>

                        <div className="form-group col-md-6">
                          <label>Sitio Web</label>
                          <input
                            {...register('sitio_web')}
                            className="form-control"
                            placeholder="https://www.miinmobiliaria.com"
                          />
                          <small className="text-danger">{errors.sitio_web?.message}</small>
                        </div>

                        <div className="form-group col-md-6">
                          <label>Ubicación en Google Maps (URL)</label>
                          <input
                            {...register('direccion')}
                            className="form-control"
                            placeholder="https://maps.google.com/..."
                          />
                          <small className="text-danger">{errors.direccion?.message}</small>
                        </div>
                      </div>
                    </div>

                    {/* Social Networks */}
                    <div className="form-submit">
                      <h3>Redes Sociales</h3>
                      <div className="submit-section row">
                        <div className="form-group col-md-6">
                          <label>Facebook</label>
                          <input
                            {...register('redes.facebook')}
                            className="form-control"
                            placeholder="https://facebook.com/..."
                          />
                          <small className="text-danger">{errors.redes?.facebook?.message}</small>
                        </div>
                        <div className="form-group col-md-6">
                          <label>Instagram</label>
                          <input
                            {...register('redes.instagram')}
                            className="form-control"
                            placeholder="https://instagram.com/..."
                          />
                          <small className="text-danger">{errors.redes?.instagram?.message}</small>
                        </div>
                      </div>
                    </div>

                    <div className="form-group col-12 mt-4">
                      <div className="d-flex gap-3">
                        <button
                          className={`btn btn-primary px-5 rounded ${!hasChanges ? 'opacity-75' : ''}`}
                          type="submit"
                        >
                          <i className="fa-solid fa-save me-2"></i>
                          Guardar Cambios
                        </button>
                        <button
                          type="button"
                          className="btn btn-secondary px-5 rounded"
                          onClick={() => navigate(-1)}
                        >
                          <i className="fas fa-arrow-left me-2"></i>
                          Regresar
                        </button>
                      </div>
                    </div>
                  </div>
                </form>
              </div>
            )}

            {/* TAB 2: Afiliados */}
            {activeTab === 'afiliados' && (
              <div className="tab-pane fade show active">
                <div className="bg-white p-4 rounded shadow-sm">
                  <div className="d-flex justify-content-between align-items-center mb-4">
                    <h3 className="mb-0">
                      <i className="fa-solid fa-users me-2"></i>
                      Gestión de Afiliados
                    </h3>
                    <button
                      className="btn btn-success"
                      onClick={() => navigate(`/add-agent?inmobiliaria=${id}`)}
                    >
                      <i className="fa-solid fa-plus me-2"></i>
                      Agregar Afiliado
                    </button>
                  </div>

                  {/* Filters */}
                  <div className="row mb-3">
                    <div className="col-md-6">
                      <input
                        type="text"
                        className="form-control search-input"
                        placeholder="Buscar por nombre..."
                        value={filtroAfiliado}
                        onChange={(e) => {
                          setFiltroAfiliado(e.target.value);
                          setPaginaAfiliados(1);
                        }}
                      />
                    </div>
                    <div className="col-md-6">
                      <select
                        className="form-select"
                        value={filtroEstadoAfiliado}
                        onChange={(e) => {
                          setFiltroEstadoAfiliado(e.target.value as any);
                          setPaginaAfiliados(1);
                        }}
                      >
                        <option value="todos">Todos los estados</option>
                        <option value="activos">Solo activos</option>
                        <option value="inactivos">Solo inactivos</option>
                      </select>
                    </div>
                  </div>

                  {/* Table */}
                  {afiliadosFiltrados.length === 0 ? (
                    <div className="alert alert-info">
                      <i className="fa-solid fa-info-circle me-2"></i>
                      No hay afiliados registrados en esta inmobiliaria.
                    </div>
                  ) : (
                    <>
                      <div className="table-responsive">
                        <table className="table table-bordered table-hover">
                          <thead className="table-light">
                            <tr>
                              <th>Foto</th>
                              <th>Nombre</th>
                              <th>Correo</th>
                              <th>Teléfono</th>
                              <th className="text-center">Estado</th>
                              <th className="text-center">Acciones</th>
                            </tr>
                          </thead>
                          <tbody>
                            {afiliadosPaginados.map(afiliado => (
                              <tr key={afiliado.id}>
                                <td>
                                  {afiliado.foto ? (
                                    <img src={afiliado.foto} alt={afiliado.nombre} className="table-foto" />
                                  ) : (
                                    <div className="table-foto bg-light d-flex align-items-center justify-content-center">
                                      <i className="fa-solid fa-user text-muted"></i>
                                    </div>
                                  )}
                                </td>
                                <td>{afiliado.nombre}</td>
                                <td>{afiliado.correo}</td>
                                <td>{afiliado.telefono}</td>
                                <td className="text-center">
                                  <button
                                    className={`btn btn-sm ${afiliado.activo ? 'btn-success' : 'btn-secondary'}`}
                                    onClick={() => handleToggleMemberStatus(afiliado.id, afiliado.activo, 'asesor')}
                                    title={afiliado.activo ? 'Click para desactivar' : 'Click para activar'}
                                  >
                                    <i className={`fa-solid fa-${afiliado.activo ? 'check-circle' : 'ban'} me-1`}></i>
                                    {afiliado.activo ? 'Activo' : 'Inactivo'}
                                  </button>
                                </td>
                                <td className="text-center">
                                  <div className="dropdown">
                                    <button
                                      className="btn btn-sm btn-outline-secondary dropdown-toggle"
                                      type="button"
                                      data-bs-toggle="dropdown"
                                    >
                                      <i className="fa-solid fa-gear"></i>
                                    </button>
                                    <ul className="dropdown-menu">
                                      <li>
                                        <button
                                          className="dropdown-item"
                                          onClick={() => navigate(`/ver-admin-afiliado/${afiliado.id}`)}
                                        >
                                          <i className="fa-solid fa-eye me-2"></i>Ver Detalles Admin
                                        </button>
                                      </li>
                                      {isAdmin && (
                                        <>
                                          <li>
                                            <button
                                              className="dropdown-item"
                                              onClick={() => navigate(`/editar-afiliado/${afiliado.id}`)}
                                            >
                                              <i className="fa-solid fa-pen-to-square me-2"></i>Editar
                                            </button>
                                          </li>
                                          <li><hr className="dropdown-divider" /></li>
                                          <li>
                                            <button
                                              className="dropdown-item text-danger"
                                              onClick={() => handleDeleteMember(afiliado.id, afiliado.nombre, 'asesor')}
                                            >
                                              <i className="fa-solid fa-trash me-2"></i>Eliminar
                                            </button>
                                          </li>
                                        </>
                                      )}
                                    </ul>
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>

                      {/* Pagination */}
                      {totalPaginasAfiliados > 1 && (
                        <nav className="d-flex justify-content-center mt-3">
                          <ul className="pagination">
                            {Array.from({ length: totalPaginasAfiliados }, (_, i) => (
                              <li
                                key={i}
                                className={`page-item ${paginaAfiliados === i + 1 ? 'active' : ''}`}
                                onClick={() => setPaginaAfiliados(i + 1)}
                                style={{ cursor: 'pointer' }}
                              >
                                <span className="page-link">{i + 1}</span>
                              </li>
                            ))}
                          </ul>
                        </nav>
                      )}
                    </>
                  )}
                </div>
              </div>
            )}

            {/* TAB 3: Asociados */}
            {activeTab === 'asociados' && (
              <div className="tab-pane fade show active">
                <div className="bg-white p-4 rounded shadow-sm">
                  <div className="d-flex justify-content-between align-items-center mb-4">
                    <h3 className="mb-0">
                      <i className="fa-solid fa-user-tie me-2"></i>
                      {isAdmin ? 'Gestión de Asociados' : 'Asociados de la Agencia'}
                    </h3>
                    {isAdmin && (
                      <button
                        className="btn btn-success"
                        onClick={() => navigate(`/add-aso?inmobiliaria=${id}`)}
                      >
                        <i className="fa-solid fa-plus me-2"></i>
                        Agregar Asociado
                      </button>
                    )}
                  </div>

                  {/* Filters */}
                  <div className="row mb-3">
                    <div className="col-md-6">
                      <input
                        type="text"
                        className="form-control search-input"
                        placeholder="Buscar por nombre..."
                        value={filtroAsociado}
                        onChange={(e) => {
                          setFiltroAsociado(e.target.value);
                          setPaginaAsociados(1);
                        }}
                      />
                    </div>
                    <div className="col-md-6">
                      <select
                        className="form-select"
                        value={filtroEstadoAsociado}
                        onChange={(e) => {
                          setFiltroEstadoAsociado(e.target.value as any);
                          setPaginaAsociados(1);
                        }}
                      >
                        <option value="todos">Todos los estados</option>
                        <option value="activos">Solo activos</option>
                        <option value="inactivos">Solo inactivos</option>
                      </select>
                    </div>
                  </div>

                  {/* Table */}
                  {asociadosFiltrados.length === 0 ? (
                    <div className="alert alert-info">
                      <i className="fa-solid fa-info-circle me-2"></i>
                      No hay asociados registrados en esta inmobiliaria.
                    </div>
                  ) : (
                    <>
                      <div className="table-responsive">
                        <table className="table table-bordered table-hover">
                          <thead className="table-light">
                            <tr>
                              <th>Foto</th>
                              <th>Nombre</th>
                              <th>Correo</th>
                              <th>Teléfono</th>
                              <th className="text-center">Estado</th>
                              <th className="text-center">Acciones</th>
                            </tr>
                          </thead>
                          <tbody>
                            {asociadosPaginados.map(asociado => (
                              <tr key={asociado.id}>
                                <td>
                                  {asociado.foto ? (
                                    <img src={asociado.foto} alt={asociado.nombre} className="table-foto" />
                                  ) : (
                                    <div className="table-foto bg-light d-flex align-items-center justify-content-center">
                                      <i className="fa-solid fa-user text-muted"></i>
                                    </div>
                                  )}
                                </td>
                                <td>{asociado.nombre}</td>
                                <td>{asociado.correo}</td>
                                <td>{asociado.telefono}</td>
                                <td className="text-center">
                                  {isAdmin ? (
                                    <button
                                      className={`btn btn-sm ${asociado.activo ? 'btn-success' : 'btn-secondary'}`}
                                      onClick={() => handleToggleMemberStatus(asociado.id, asociado.activo, 'asociado')}
                                      title={asociado.activo ? 'Click para desactivar' : 'Click para activar'}
                                    >
                                      <i className={`fa-solid fa-${asociado.activo ? 'check-circle' : 'ban'} me-1`}></i>
                                      {asociado.activo ? 'Activo' : 'Inactivo'}
                                    </button>
                                  ) : (
                                    <span className={`badge ${asociado.activo ? 'bg-success' : 'bg-secondary'}`}>
                                      {asociado.activo ? 'Activo' : 'Inactivo'}
                                    </span>
                                  )}
                                </td>
                                <td className="text-center">
                                  {isAdmin ? (
                                    <div className="dropdown">
                                      <button
                                        className="btn btn-sm btn-outline-secondary dropdown-toggle"
                                        type="button"
                                        data-bs-toggle="dropdown"
                                      >
                                        <i className="fa-solid fa-gear"></i>
                                      </button>
                                      <ul className="dropdown-menu">
                                        <li>
                                          <button
                                            className="dropdown-item"
                                            onClick={() => navigate(`/ver-admin-asociado/${asociado.id}`)}
                                          >
                                            <i className="fa-solid fa-eye me-2"></i>Ver Detalles Admin
                                          </button>
                                        </li>
                                        <li>
                                          <button
                                            className="dropdown-item"
                                            onClick={() => navigate(`/edit-aso/${asociado.id}`)}
                                          >
                                            <i className="fa-solid fa-pen-to-square me-2"></i>Editar
                                          </button>
                                        </li>
                                        <li><hr className="dropdown-divider" /></li>
                                        <li>
                                          <button
                                            className="dropdown-item text-danger"
                                            onClick={() => handleDeleteMember(asociado.id, asociado.nombre, 'asociado')}
                                          >
                                            <i className="fa-solid fa-trash me-2"></i>Eliminar
                                          </button>
                                        </li>
                                      </ul>
                                    </div>
                                  ) : (
                                    <button
                                      className="btn btn-sm btn-outline-primary"
                                      onClick={() => navigate(`/asociado-page/${asociado.id}`)}
                                      title="Ver perfil"
                                    >
                                      <i className="fa-solid fa-eye me-1"></i>
                                      Ver
                                    </button>
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>

                      {/* Pagination */}
                      {totalPaginasAsociados > 1 && (
                        <nav className="d-flex justify-content-center mt-3">
                          <ul className="pagination">
                            {Array.from({ length: totalPaginasAsociados }, (_, i) => (
                              <li
                                key={i}
                                className={`page-item ${paginaAsociados === i + 1 ? 'active' : ''}`}
                                onClick={() => setPaginaAsociados(i + 1)}
                                style={{ cursor: 'pointer' }}
                              >
                                <span className="page-link">{i + 1}</span>
                              </li>
                            ))}
                          </ul>
                        </nav>
                      )}
                    </>
                  )}
                </div>
              </div>
            )}

            {/* TAB 4: Propiedades */}
            {activeTab === 'propiedades' && (
              <div className="tab-pane fade show active">
                <div className="bg-white p-4 rounded shadow-sm">
                  <div className="d-flex justify-content-between align-items-center mb-4">
                    <h3 className="mb-0">
                      <i className="fa-solid fa-home me-2"></i>
                      Propiedades de la Inmobiliaria
                    </h3>
                    <button
                      className="btn btn-success"
                      onClick={() => navigate('/submit-property')}
                    >
                      <i className="fa-solid fa-plus me-2"></i>
                      Publicar Propiedad
                    </button>
                  </div>
                  {propiedades.length === 0 ? (
                    <div className="alert alert-info">
                      <i className="fa-solid fa-info-circle me-2"></i>
                      No hay propiedades registradas para esta inmobiliaria.
                    </div>
                  ) : (
                    <div className="row">
                      {propiedades.map(prop => (
                        <div key={prop.id} className="col-md-6 col-lg-4 mb-4">
                          <div className="card h-100 shadow-sm">
                            {prop.imagen && (
                              <img
                                src={prop.imagen}
                                className="card-img-top"
                                alt={prop.titulo}
                                style={{ height: '200px', objectFit: 'cover' }}
                              />
                            )}
                            <div className="card-body">
                              <h5 className="card-title">{prop.titulo}</h5>
                              <p className="text-muted mb-2">
                                <i className="fa-solid fa-tag me-2"></i>
                                {prop.tipo}
                              </p>
                              <p className="text-muted mb-2">
                                <i className="fa-solid fa-location-dot me-2"></i>
                                {prop.ciudad}
                              </p>
                              <p className="text-primary fw-bold">
                                <i className="fa-solid fa-dollar-sign me-2"></i>
                                {prop.precio?.toLocaleString()}
                              </p>
                              <button
                                className="btn btn-sm btn-outline-primary w-100"
                                onClick={() => navigate(`/single-property-1/${prop.id}`)}
                              >
                                <i className="fa-solid fa-eye me-2"></i>
                                Ver Detalles
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* TAB 5: Proyectos */}
            {activeTab === 'proyectos' && (
              <div className="tab-pane fade show active">
                <div className="bg-white p-4 rounded shadow-sm">
                  <h3 className="mb-4">
                    <i className="fa-solid fa-building me-2"></i>
                    Proyectos de la Inmobiliaria
                  </h3>
                  {proyectos.length === 0 ? (
                    <div className="alert alert-info">
                      <i className="fa-solid fa-info-circle me-2"></i>
                      No hay proyectos registrados para esta inmobiliaria.
                    </div>
                  ) : (
                    <div className="row">
                      {proyectos.map(proy => (
                        <div key={proy.id} className="col-md-6 col-lg-4 mb-4">
                          <div className="card h-100 shadow-sm">
                            {proy.imagen && (
                              <img
                                src={proy.imagen}
                                className="card-img-top"
                                alt={proy.nombre}
                                style={{ height: '200px', objectFit: 'cover' }}
                              />
                            )}
                            <div className="card-body">
                              <h5 className="card-title">{proy.nombre}</h5>
                              <p className="text-muted mb-2">
                                {proy.descripcion?.substring(0, 100)}...
                              </p>
                              <p className="text-muted mb-3">
                                <i className="fa-solid fa-location-dot me-2"></i>
                                {proy.ubicacion}
                              </p>
                              <button
                                className="btn btn-sm btn-outline-primary w-100"
                                onClick={() => navigate(`/project-detail/${proy.id}`)}
                              >
                                <i className="fa-solid fa-eye me-2"></i>
                                Ver Detalles
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      <FooterTop bg="theme-bg" />
      <Footer />
    </>
  );
}
